import type { BadgeId, ProfileIconId } from '@pebblecode/shared';
import type { ReactNode } from 'react';

const GLYPHS: Record<ProfileIconId | 'check', ReactNode> = {
  pebble: <path d="M5 14c0-4 3.2-8 8-8 4.2 0 7 3 7 7s-3.3 6-8 6c-4 0-7-2-7-5z" />,
  star: <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6-4.5-4.2 6.1-.7z" />,
  bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  rocket: (
    <>
      <path d="M12 2c3 2 5 6 5 10l-2 4H9l-2-4c0-4 2-8 5-10z" />
      <path d="M9 16l-3 3h4M15 16l3 3h-4" />
      <circle cx="12" cy="10" r="1.6" />
    </>
  ),
  crown: <path d="M3 8l4 4 5-7 5 7 4-4-2 11H5z" />,
  flame: <path d="M12 2c1 4 6 6 6 12a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-3 0-6 1-9z" />,
  brain: (
    <>
      <path d="M10 4a3 3 0 0 0-3.4 2.6A3 3 0 0 0 4.5 12a3 3 0 0 0 2 5A3 3 0 0 0 10 20z" />
      <path d="M14 4a3 3 0 0 1 3.4 2.6 3 3 0 0 1 2.1 5.4 3 3 0 0 1-2 5A3 3 0 0 1 14 20z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3.2 3.4 3.2 14.6 0 18M12 3c-3.2 3.4-3.2 14.6 0 18" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
};

export function ProfileIcon({ id, size = 18 }: { id: ProfileIconId; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPHS[id]}
    </svg>
  );
}

const BADGE_LOOK: Record<BadgeId, { glyph: keyof typeof GLYPHS; color: string }> = {
  'first-solve': { glyph: 'star', color: 'var(--accent)' },
  'solver-10': { glyph: 'star', color: 'var(--cat-math)' },
  'hard-one': { glyph: 'bolt', color: 'var(--cat-variables)' },
  'no-hint-5': { glyph: 'brain', color: 'var(--cat-text)' },
  'category-sequences': { glyph: 'check', color: 'var(--cat-io)' },
  'category-conditions': { glyph: 'check', color: 'var(--cat-logic)' },
  'category-loops': { glyph: 'check', color: 'var(--cat-loops)' },
  'category-digits': { glyph: 'check', color: 'var(--cat-math)' },
  'category-arrays': { glyph: 'check', color: 'var(--cat-lists)' },
  'category-strings': { glyph: 'check', color: 'var(--cat-text)' },
  'all-exercises': { glyph: 'crown', color: 'var(--accent)' },
  'streak-3': { glyph: 'flame', color: '#E08A3A' },
  'streak-7': { glyph: 'flame', color: '#D9663A' },
  'streak-30': { glyph: 'flame', color: '#B5526B' },
  polyglot: { glyph: 'globe', color: 'var(--cat-logic)' },
  stepper: { glyph: 'pebble', color: 'var(--step-text)' },
  'live-learner': { glyph: 'rocket', color: 'var(--live-dot)' },
};

/** A round medal; greyed out while not earned. */
export function BadgeMedal({
  id,
  earned,
  size = 44,
}: {
  id: BadgeId;
  earned: boolean;
  size?: number;
}) {
  const look = BADGE_LOOK[id];
  return (
    <span
      className={`medal${earned ? '' : ' locked'}`}
      style={{ width: size, height: size, ...(earned ? { background: look.color } : {}) }}
      aria-hidden="true"
    >
      <svg
        width={size * 0.5}
        height={size * 0.5}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {GLYPHS[look.glyph]}
      </svg>
    </span>
  );
}
